import { useEffect, useRef, useState, SetStateAction, Dispatch } from 'react';
import { useLocation } from 'react-router-dom';
import block from 'bem-cn';
import { observer } from 'mobx-react';
import { Vector2, PointLight, AmbientLight, Clock, Spherical, Vector3, TextureLoader, Color } from 'three';

import { charactersStore, sceneStore } from 'stores';
import motionStore from 'stores/motionStore';

import { Loader } from 'components';
import { ReactComponent as Arrow } from '../../Icons/arrow.svg';
import SceneDashboard from './components/Dashboard/Dashboard';
import HelpTooltip from './components/HelpTooltip/HelpTooltip';
import Tooltip from './components/Tooltip/Tooltip';

import './ScenePage2.scss';

const cnScenePage = block('ScenePage2');

interface ILevel {
  recordId: string;
  level: string;
  createdAt?: string;
}

interface ILocationState {
  recordId: string;
  level: string;
  createdAt?: string;
}

const ScenePage2 = () => {
  const sceneWrapperRef = useRef<HTMLDivElement>(null);
  const selectedChar = charactersStore.selectedCharacter;

  const location = useLocation();
  const state = location.state as ILocationState | undefined;

  const [currentLevel, setCurrentLevel] = useState<string>(
      state?.level || (selectedChar?.level ? String(selectedChar.level) : '1')
  );
  const [currentRecordId, setCurrentRecordId] = useState<string | null>(state?.recordId || null);
  const [currentData, setCurrentData] = useState('first');
  const [stopChecking, setStopChecking] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hoveredName, setHoveredName] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const ticking = useRef(false);
  const animationFrameIdRef = useRef<number>();
  const clockRef = useRef(new Clock());
  const frameTimerRef = useRef(0);
  const frameInterval = 1 / 30; // 30 FPS

  // Мышь и пересечение
  const mouseDownEvent = () => setStopChecking(true);
  const mouseUpEvent = () => setStopChecking(false);

  const checkIntersection = (e: PointerEvent) => {
    if (stopChecking || !sceneWrapperRef.current || !e.isPrimary) return;

    const x = (e.offsetX / sceneWrapperRef.current.offsetWidth) * 2 - 1;
    const y = -(e.offsetY / sceneWrapperRef.current.offsetHeight) * 2 + 1;

    if (!ticking.current) {
      ticking.current = true;
      requestAnimationFrame(() => {
        sceneStore.intersection = [new Vector2(x, y)];
        setPosition({ x: e.offsetX, y: e.offsetY });

        if (sceneStore.bodyModel) {
          const name = sceneStore.bodyModel.checkIntersection(new Vector2(x, y));
          setHoveredName(name);
        }

        ticking.current = false;
      });
    }
  };

  // Анимация сцены
  const animate = () => {
    animationFrameIdRef.current = requestAnimationFrame(animate);

    const delta = clockRef.current.getDelta();
    frameTimerRef.current += delta;

    if (frameTimerRef.current >= frameInterval) {
      frameTimerRef.current -= frameInterval;
      motionStore.nextFrame();
    }

    motionStore.update(delta);
    sceneStore.controls.update();

    if (sceneStore.composer) sceneStore.composer.render();
    else sceneStore.renderer?.render(sceneStore.scene, sceneStore.camera);
  };

  // Логирование CSV
  useEffect(() => {
    if (charactersStore.characterDataCSV) {
      console.log('📦 CSV данные обновлены:', charactersStore.characterDataCSV);
    }
  }, [charactersStore.characterDataCSV]);

  // Инициализация сцены и загрузка motion
  useEffect(() => {
    const wrapper = sceneWrapperRef.current;
    if (!wrapper) return;

    if (!sceneStore.bodyModel) {
      sceneStore.clearScene();
      sceneStore.initScene(wrapper);
    }

    // фон — просто картинка
    const loader = new TextureLoader();
    loader.load('/images/floor.jpeg',
        (texture) => {
          sceneStore.scene.background = texture;
        },
        undefined,
        (err) => {
          console.warn('⚠ Не удалось загрузить фон, fallback на цвет');
          sceneStore.scene.background = new Color(0x87ceeb); // небесно-голубой
        }
    );

    const ambientLight = new AmbientLight(0xffffff, 0.5);
    sceneStore.scene.add(ambientLight);
    const pointLight = new PointLight(0xffffff, 1);
    pointLight.position.set(5, 5, 5);
    sceneStore.addLight(pointLight);

    wrapper.addEventListener('pointermove', checkIntersection);
    wrapper.addEventListener('mousedown', mouseDownEvent);
    wrapper.addEventListener('mouseup', mouseUpEvent);

    const initMotion = async () => {
      console.log('🔹 initMotion called', { selectedChar, currentLevel, currentRecordId });

      if (!selectedChar || !currentLevel || !currentRecordId) {
        console.warn('⚠ initMotion skipped: missing data', {
          selectedChar,
          currentLevel,
          currentRecordId,
        });
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        console.log('🔹 JWT token:', token);

        const csvData = await charactersStore.loadProcessedCSVForLevel(currentRecordId);

        console.log('📦 CSV rows count:', charactersStore.characterDataCSV.length);

        if (!charactersStore.characterDataCSV || charactersStore.characterDataCSV.length === 0) {
          console.warn('⚠ CSV пустой! MotionStore анимация не будет работать');
          return;
        }

        console.log('✅ CSV загружен для recordId:', currentRecordId, 'rows:', charactersStore.characterDataCSV.length);

        await motionStore.loadCSV(charactersStore.characterDataCSV);
        console.log('✅ MotionStore CSV загружен');

        clockRef.current.start();
        animate();
      } catch (err) {
        console.error('❌ Ошибка загрузки motion CSV:', err);
        setError(true);
      }
    };

    initMotion();

    return () => {
      if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
      sceneStore.fullDeInit();
      wrapper.removeEventListener('pointermove', checkIntersection);
      wrapper.removeEventListener('mousedown', mouseDownEvent);
      wrapper.removeEventListener('mouseup', mouseUpEvent);
      console.log('🔹 ScenePage2 cleanup done');
    };
  }, [selectedChar, currentLevel, currentRecordId]);

  // Resize сцены
  useEffect(() => {
    const onResize = () => {
      if (!sceneWrapperRef.current) return;
      sceneStore.renderer.setSize(
          sceneWrapperRef.current.clientWidth,
          sceneWrapperRef.current.clientHeight
      );
      sceneStore.camera.aspect =
          sceneWrapperRef.current.clientWidth / sceneWrapperRef.current.clientHeight;
      sceneStore.camera.updateProjectionMatrix();
      if (sceneStore.composer)
        sceneStore.composer.setSize(
            sceneWrapperRef.current.clientWidth,
            sceneWrapperRef.current.clientHeight
        );
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Вращение камеры
  const changeCameraPosition = (direction: 'left' | 'right' | 'top' | 'bottom') => {
    if (!sceneStore.controls || !sceneStore.camera) return;
    const offset = sceneStore.camera.position.clone().sub(sceneStore.controls.target);
    const spherical = new Spherical();
    spherical.setFromVector3(offset);

    const startTheta = spherical.theta;
    const startPhi = spherical.phi;
    let targetTheta = startTheta;
    let targetPhi = startPhi;
    const angle = Math.PI / 2;

    if (direction === 'left') targetTheta -= angle;
    else if (direction === 'right') targetTheta += angle;
    else if (direction === 'top') targetPhi = Math.max(0.1, startPhi - angle);
    else if (direction === 'bottom') targetPhi = Math.min(Math.PI - 0.1, startPhi + angle);

    let startTime: number | null = null;
    const duration = 0.8;

    const animateRotation = (time: number) => {
      if (!startTime) startTime = time;
      const elapsed = (time - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      const easing = Math.sin((progress * Math.PI) / 2);

      spherical.theta = startTheta + (targetTheta - startTheta) * easing;
      spherical.phi = startPhi + (targetPhi - startPhi) * easing;

      const newPos = new Vector3().setFromSpherical(spherical).add(sceneStore.controls.target);
      sceneStore.camera.position.copy(newPos);
      sceneStore.controls.update();

      if (progress < 1) requestAnimationFrame(animateRotation);
    };

    requestAnimationFrame(animateRotation);
  };

  if (!selectedChar) {
    if (charactersStore.isLoading) return <Loader visible />;

    return (
        <div className="scene-pageWrapper" style={{ width: '100vw', height: '100vh' }}>
          <p style={{ textAlign: 'center', marginTop: 50, fontSize: 18 }}>
            Сначала выберите персонажа на главной странице
          </p>
        </div>
    );
  }

  return (
      <div className="scene-pageWrapper" style={{ width: '100vw', height: '100%' }}>
        <SceneDashboard
            showHelp={showHelp}
            currentStep={currentStep}
            selectedCharacter={selectedChar}
            currentData={currentData}
            currentLevel={currentLevel}
            setCurrentLevel={(lvl: SetStateAction<string>) => setCurrentLevel(String(lvl))}
            setCurrentData={setCurrentData as Dispatch<SetStateAction<string>>}
            onLevelSelect={(level: ILevel) => {
              if (!level.recordId) return;
              setCurrentRecordId(level.recordId);
              charactersStore.selectLevel(level.level, level.createdAt);
              setCurrentLevel(String(level.level));
            }}
        />

        {showHelp && (
            <HelpTooltip
                setShowHelp={setShowHelp}
                steps={[1, 2, 3, 4, 5]}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
            />
        )}

        <div
            className={`${cnScenePage()} ${
                showHelp && (currentStep === 1 || currentStep === 2) ? 'showBlur' : ''
            }`}
            ref={sceneWrapperRef}
            style={{ width: '100%', height: '100%' }}
        >
          {error && <div className="error">Ошибка загрузки motion CSV</div>}
          {sceneStore.isLoading && <Loader visible={sceneStore.isLoading} />}

          {sceneStore.showButtons && (
              <>
                <div className="TestBtn top" onClick={() => changeCameraPosition('top')}>
                  Вращать вверх <Arrow className="logo1" />
                </div>
                <div className="TestBtn right" onClick={() => changeCameraPosition('right')}>
                  Вращать вправо <Arrow className="logo1" />
                </div>
                <div className="TestBtn left" onClick={() => changeCameraPosition('left')}>
                  Вращать влево <Arrow className="logo1" />
                </div>
                <div className="TestBtn bot" onClick={() => changeCameraPosition('bottom')}>
                  Вращать вниз <Arrow className="logo1" />
                </div>
              </>
          )}

          {hoveredName && <Tooltip name={hoveredName} position={position} />}
        </div>
      </div>
  );
};

export default observer(ScenePage2);
